import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ChevronRight, Trash2 } from 'lucide-react'
import { api } from '@/lib/api/client'
import {
  REVENUE_CATEGORIES,
  TAX_ZONES,
  type CustomerTaxZone,
  type RevenueAccountMatrixEntry,
  type RevenueAccountOption,
  type RevenueCategory,
} from '@/types/api'

type CellKey = `${RevenueCategory}|${CustomerTaxZone}`

function cellKey(category: RevenueCategory, zone: CustomerTaxZone): CellKey {
  return `${category}|${zone}` as CellKey
}

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 6, fontSize: 13, color: 'var(--ns-text-muted)', marginBottom: 20 }}>
      <Link to="/settings/integrations" style={{ color: 'var(--ns-text-muted)', textDecoration: 'none' }}>Settings</Link>
      <ChevronRight size={14} />
      <span style={{ color: 'var(--ns-text)' }}>Revenue Account Matrix</span>
    </div>
  )
}

export default function RevenueAccountMatrixPage() {
  const queryClient = useQueryClient()

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['admin', 'revenue-account-matrix'],
    queryFn:  () => api.get<RevenueAccountMatrixEntry[]>('/settings/revenue-account-matrix'),
  })

  const { data: accountOptions = [], isLoading: accountsLoading } = useQuery({
    queryKey: ['admin', 'revenue-account-matrix', 'accounts'],
    queryFn:  () => api.get<RevenueAccountOption[]>('/settings/revenue-account-matrix/accounts'),
  })

  const upsert = useMutation({
    mutationFn: (body: {
      revenueCategory: RevenueCategory
      customerTaxZone: CustomerTaxZone
      fortnoxAccountNumber: number
      description: string | null
    }) => api.put<RevenueAccountMatrixEntry>('/settings/revenue-account-matrix', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'revenue-account-matrix'] }),
  })

  const remove = useMutation({
    mutationFn: ({ category, zone }: { category: RevenueCategory; zone: CustomerTaxZone }) =>
      api.delete<unknown>(`/settings/revenue-account-matrix?revenueCategory=${category}&customerTaxZone=${zone}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'revenue-account-matrix'] }),
  })

  const cellMap = useMemo(() => {
    const m = new Map<CellKey, RevenueAccountMatrixEntry>()
    for (const e of entries) m.set(cellKey(e.revenueCategory, e.customerTaxZone), e)
    return m
  }, [entries])

  const totalCells   = REVENUE_CATEGORIES.length * TAX_ZONES.length
  const filledCells  = cellMap.size
  const missingCount = totalCells - filledCells

  return (
    <div className="page">
      <Breadcrumb />
      <div className="page-header">
        <div>
          <h1 className="page-title">Revenue Account Matrix</h1>
          <p className="page-subtitle">
            Map each revenue category and customer tax zone to a Fortnox revenue account.
            Invoice generation will fail with a clear error if a needed cell is missing.
          </p>
        </div>
      </div>

      <div className="row" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="tabular muted" style={{ fontSize: 12 }}>
          {filledCells} of {totalCells} cells configured
        </span>
        {missingCount > 0 && (
          <span className="row" style={{ gap: 6, fontSize: 12, color: 'var(--ns-warn)' }}>
            <AlertTriangle size={14} />
            {missingCount} unfilled {missingCount === 1 ? 'cell' : 'cells'}
          </span>
        )}
      </div>

      {isLoading || accountsLoading ? (
        <p className="muted" style={{ textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Revenue category</th>
                {TAX_ZONES.map(z => (
                  <th key={z.value} style={{ minWidth: 260 }}>{z.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {REVENUE_CATEGORIES.map(cat => (
                <tr key={cat.value}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{cat.label}</div>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{cat.description}</div>
                  </td>
                  {TAX_ZONES.map(zone => {
                    const existing = cellMap.get(cellKey(cat.value, zone.value))
                    return (
                      <MatrixCell
                        key={zone.value}
                        category={cat.value}
                        zone={zone.value}
                        entry={existing}
                        options={accountOptions}
                        onSave={(accountNumber, description) =>
                          upsert.mutate({
                            revenueCategory: cat.value,
                            customerTaxZone: zone.value,
                            fortnoxAccountNumber: accountNumber,
                            description,
                          })
                        }
                        onClear={() => {
                          if (!confirm(`Clear ${cat.label} × ${zone.label}?`)) return
                          remove.mutate({ category: cat.value, zone: zone.value })
                        }}
                        saving={upsert.isPending}
                      />
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function MatrixCell({
  category, zone, entry, options, onSave, onClear, saving,
}: {
  category: RevenueCategory
  zone: CustomerTaxZone
  entry: RevenueAccountMatrixEntry | undefined
  options: RevenueAccountOption[]
  onSave: (accountNumber: number, description: string | null) => void
  onClear: () => void
  saving: boolean
}) {
  const [accountInput, setAccountInput]         = useState<string>(entry ? String(entry.fortnoxAccountNumber) : '')
  const [descriptionInput, setDescriptionInput] = useState<string>(entry?.description ?? '')

  // Keep local state in sync with the latest entry from the server. The grid
  // re-mounts cells on data refresh, but a controlled select needs current
  // entry as a fallback when the user has not typed.
  const currentAccount = accountInput || (entry ? String(entry.fortnoxAccountNumber) : '')

  const dirty =
    accountInput !== '' && (
      Number(accountInput) !== entry?.fortnoxAccountNumber ||
      (descriptionInput || null) !== (entry?.description ?? null)
    )

  return (
    <td style={{ verticalAlign: 'top' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <select
          className="input"
          value={currentAccount}
          onChange={e => setAccountInput(e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="">— select account —</option>
          {options.map(o => (
            <option key={o.number} value={o.number}>
              {o.number}{o.description ? ` — ${o.description}` : ''}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="Note (optional)"
          value={descriptionInput}
          onChange={e => setDescriptionInput(e.target.value)}
          style={{ width: '100%' }}
        />
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          {entry && (
            <button
              className="btn btn-icon"
              title="Clear cell"
              disabled={saving}
              onClick={onClear}
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            className="btn btn-primary btn-sm"
            disabled={!dirty || saving || !currentAccount}
            onClick={() => onSave(Number(currentAccount), descriptionInput.trim() || null)}
          >
            Save
          </button>
        </div>
        {!entry && (
          <span className="row" style={{ gap: 4, fontSize: 11, color: 'var(--ns-warn)' }}>
            <AlertTriangle size={11} />
            Not set — invoice generation for {category} × {zone} will fail.
          </span>
        )}
      </div>
    </td>
  )
}
